import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Plus, Users, Search, ExternalLink, List } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

function ConversationList({ user }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => { fetchConversations(); }, []);

  const fetchConversations = async () => {
    try {
      const response = await authFetch(`${API}/conversations`);
      if (response.ok) setConversations(await response.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const searchUsers = async (q) => {
    setSearchQuery(q);
    if (q.length < 1) { setSearchResults([]); setIsSearching(false); return; }
    setIsSearching(true);
    try {
      const response = await authFetch(`${API}/users/search?q=${encodeURIComponent(q)}`);
      if (response.ok) setSearchResults(await response.json());
    } catch (e) { console.error(e); }
  };

  const startChat = async (targetUserId) => {
    try {
      const response = await authFetch(`${API}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant_ids: [targetUserId], is_group: false })
      });
      if (response.ok) {
        const conv = await response.json();
        navigate(`/messages/${conv.conversation_id}`);
      }
    } catch (e) { toast.error('Failed to start chat'); }
  };

  const getConvName = (conv) => {
    if (conv.is_group && conv.name) return conv.name;
    const other = conv.participant_details?.find(p => p.user_id !== user?.user_id);
    return other?.name || 'Chat';
  };

  const getConvPicture = (conv) => {
    const other = conv.participant_details?.find(p => p.user_id !== user?.user_id);
    return other?.picture;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div>
      {/* Search Bar */}
      <div className="px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            data-testid="messages-search-bar"
            type="text"
            value={searchQuery}
            onChange={(e) => searchUsers(e.target.value)}
            placeholder="Search users to message..."
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-border focus:border-primary outline-none bg-gray-50 text-sm"
          />
        </div>
      </div>

      {/* Search Results */}
      {isSearching && (
        <div className="px-4 pb-3">
          <p className="text-xs text-muted-foreground mb-2 px-1">Search results</p>
          <div className="bg-white rounded-xl border border-border overflow-hidden">
            {searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No users found</p>
            ) : (
              searchResults.map(u => (
                <button
                  key={u.user_id}
                  data-testid={`start-chat-${u.user_id}`}
                  onClick={() => startChat(u.user_id)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-border last:border-b-0"
                >
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                      {u.name?.[0]}
                    </div>
                  )}
                  <div className="text-left">
                    <p className="font-medium text-sm">{u.name}</p>
                    <p className="text-xs text-muted-foreground">@{u.username}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Conversation List */}
      {!isSearching && (
        <div className="px-4">
          {conversations.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-2">No messages yet</p>
              <p className="text-sm text-muted-foreground">Search for a user above to start chatting</p>
            </div>
          ) : (
            <div className="space-y-1">
              {conversations.map(conv => (
                <button
                  key={conv.conversation_id}
                  data-testid={`conv-${conv.conversation_id}`}
                  onClick={() => navigate(`/messages/${conv.conversation_id}`)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
                >
                  {conv.is_group ? (
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                  ) : getConvPicture(conv) ? (
                    <img src={getConvPicture(conv)} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-lg shrink-0">
                      {getConvName(conv)?.[0]}
                    </div>
                  )}
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold truncate text-sm">{getConvName(conv)}</p>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center shrink-0 ml-2">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{conv.last_message || 'No messages yet'}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChatView({ conversationId, user }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sharedChecklists, setSharedChecklists] = useState({});
  const [showChecklistPicker, setShowChecklistPicker] = useState(false);
  const [userChecklists, setUserChecklists] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversation();
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversation = async () => {
    try {
      const response = await authFetch(`${API}/conversations`);
      if (response.ok) {
        const convs = await response.json();
        setConversation(convs.find(c => c.conversation_id === conversationId));
      }
    } catch (e) { console.error(e); }
  };

  const fetchMessages = async () => {
    try {
      const response = await authFetch(`${API}/conversations/${conversationId}/messages`);
      if (response.ok) {
        const msgs = await response.json();
        setMessages(msgs.reverse());
        // Fetch shared checklist data for any messages that have shared_checklist_id
        for (const msg of msgs) {
          if (msg.shared_checklist_id && !sharedChecklists[msg.shared_checklist_id]) {
            fetchSharedChecklist(msg.shared_checklist_id);
          }
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchSharedChecklist = async (checklistId) => {
    try {
      const response = await authFetch(`${API}/checklists/${checklistId}/share-data`);
      if (response.ok) {
        const data = await response.json();
        setSharedChecklists(prev => ({ ...prev, [checklistId]: data }));
      }
    } catch (e) { console.error(e); }
  };

  const fetchUserChecklists = async () => {
    try {
      const response = await authFetch(`${API}/checklists`);
      if (response.ok) setUserChecklists(await response.json());
    } catch (e) { console.error(e); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    const text = newMessage.trim();
    setNewMessage('');
    try {
      const response = await authFetch(`${API}/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text })
      });
      if (response.ok) {
        const msg = await response.json();
        setMessages(prev => [...prev, msg]);
      }
    } catch (e) { toast.error('Failed to send'); }
  };

  const sendChecklist = async (checklist) => {
    try {
      const response = await authFetch(`${API}/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          content: `Shared checklist: ${checklist.name}`,
          shared_checklist_id: checklist.checklist_id 
        })
      });
      if (response.ok) {
        const msg = await response.json();
        setMessages(prev => [...prev, msg]);
        fetchSharedChecklist(checklist.checklist_id);
        // Track share
        authFetch(`${API}/analytics/track-share`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ checklist_id: checklist.checklist_id, medium: 'in_app_chat' })
        });
        toast.success('Checklist shared!');
      }
    } catch (e) { toast.error('Failed to share'); }
    setShowChecklistPicker(false);
  };

  const getChatName = () => {
    if (!conversation) return 'Chat';
    if (conversation.is_group && conversation.name) return conversation.name;
    const other = conversation.participant_details?.find(p => p.user_id !== user?.user_id);
    return other?.name || 'Chat';
  };

  const getChatPicture = () => {
    if (!conversation) return null;
    const other = conversation.participant_details?.find(p => p.user_id !== user?.user_id);
    return other?.picture;
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-background" style={{ zIndex: 50 }}>
      {/* Header */}
      <div className="shrink-0 bg-background/95 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="chat-back-btn" onClick={() => navigate('/messages')} className="p-2 hover:bg-primary/10 rounded-full shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          {getChatPicture() ? (
            <img src={getChatPicture()} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm shrink-0">
              {getChatName()?.[0]}
            </div>
          )}
          <h1 className="text-lg font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{getChatName()}</h1>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 max-w-2xl mx-auto w-full">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p>Say hi! Start the conversation.</p>
          </div>
        ) : (
          messages.map(msg => {
            const isMine = msg.sender_id === user?.user_id;
            const checklistData = msg.shared_checklist_id ? sharedChecklists[msg.shared_checklist_id] : null;
            return (
              <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                  isMine ? 'bg-primary text-white rounded-br-md' : 'bg-gray-100 text-foreground rounded-bl-md'
                }`}>
                  {conversation?.is_group && !isMine && (
                    <p className="text-xs font-semibold mb-1 opacity-80">{msg.sender_name}</p>
                  )}
                  {/* Shared Checklist Card */}
                  {msg.shared_checklist_id && checklistData && (
                    <div className={`rounded-xl p-3 mb-2 ${isMine ? 'bg-white/15' : 'bg-white border border-border'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <List className={`w-4 h-4 ${isMine ? 'text-white/80' : 'text-primary'}`} />
                        <span className={`font-bold text-sm ${isMine ? 'text-white' : 'text-foreground'}`}>
                          {checklistData.checklist?.name}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {checklistData.products?.map((product, idx) => (
                          <a
                            key={product.product_id}
                            href={product.purchase_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`shared-product-link-${product.product_id}`}
                            className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                              isMine 
                                ? 'bg-white/10 hover:bg-white/20' 
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                          >
                            <span className={`text-xs font-bold w-5 text-center ${isMine ? 'text-white/70' : 'text-muted-foreground'}`}>
                              {idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-semibold truncate ${isMine ? 'text-white' : 'text-foreground'}`}>
                                {product.name}
                              </p>
                              <p className={`text-[10px] ${isMine ? 'text-white/60' : 'text-muted-foreground'}`}>
                                {product.brand} • {product.price}
                              </p>
                            </div>
                            <ExternalLink className={`w-3 h-3 shrink-0 ${isMine ? 'text-white/50' : 'text-muted-foreground'}`} />
                          </a>
                        ))}
                      </div>
                      {checklistData.owner && (
                        <p className={`text-[10px] mt-2 ${isMine ? 'text-white/50' : 'text-muted-foreground'}`}>
                          by @{checklistData.owner.username || checklistData.owner.name}
                        </p>
                      )}
                    </div>
                  )}
                  {msg.content && !msg.shared_checklist_id && (
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                  )}
                  <p className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-muted-foreground'}`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Checklist Picker */}
      {showChecklistPicker && (
        <div className="shrink-0 bg-white border-t border-border px-4 py-3 max-h-60 overflow-y-auto">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold">Share a Checklist</p>
              <button onClick={() => setShowChecklistPicker(false)} className="text-xs text-muted-foreground">Cancel</button>
            </div>
            {userChecklists.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No checklists to share</p>
            ) : (
              <div className="space-y-2">
                {userChecklists.map(cl => (
                  <button
                    key={cl.checklist_id}
                    data-testid={`pick-checklist-${cl.checklist_id}`}
                    onClick={() => sendChecklist(cl)}
                    className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-primary/5 transition-colors text-left"
                  >
                    <List className="w-5 h-5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{cl.name}</p>
                      <p className="text-xs text-muted-foreground">{cl.products?.length || 0} products</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="shrink-0 bg-white border-t border-border px-4 py-2" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <button
            data-testid="share-checklist-btn-chat"
            onClick={() => { fetchUserChecklists(); setShowChecklistPicker(!showChecklistPicker); }}
            className="p-3 hover:bg-primary/10 rounded-full transition-colors shrink-0"
            title="Share a checklist"
          >
            <List className="w-5 h-5 text-primary" />
          </button>
          <input
            data-testid="message-input"
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 rounded-full border border-border focus:border-primary outline-none bg-gray-50 text-sm"
          />
          <button
            data-testid="send-message-btn"
            onClick={sendMessage}
            disabled={!newMessage.trim()}
            className="p-3 bg-primary text-white rounded-full disabled:opacity-40 hover:bg-primary/90 transition-colors shrink-0"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const { conversationId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`);
        if (response.ok) setUser(await response.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchUser();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (conversationId) return <ChatView conversationId={conversationId} user={user} />;

  return (
    <div className="min-h-screen pb-24">
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</h1>
        </div>
      </div>
      <div className="max-w-2xl mx-auto">
        <ConversationList user={user} />
      </div>
    </div>
  );
}
